import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { getExtension, getIdentifier, MedplumClient } from '@medplum/core';
import {
  IVR_BUSINESS_HOURS_SCHEDULE_CATEGORY,
  IVR_SYSTEM_CONFIG_BASIC_CODING,
  IVR_TEMPLATE_BASIC_CODING,
} from './codings';
import { Attachment, Basic, Extension, Location, Practitioner, Reference, Schedule } from '@medplum/fhirtypes';
import {
  calculateDurationHours,
  calculateEndTimeFromDuration,
  convertTimingDurationToHours,
  normalizeTimeToHHMMSS,
} from './time';
import {
  BARIENDO_CT_SYSTEM,
  IVR_BASIC_SYSTEM,
  IVR_HOURS_SET_DEFAULT_NAMES,
  IVR_HOURS_SET_IDENTIFIER_SYSTEM,
  IVR_TEMPLATE_IDENTIFIER_SYSTEM,
  IvrHoursSetId,
  IvrRoutingActionType,
  IvrTargetKind,
  IvrTargetScope,
  IvrTemplateId,
  LocationType,
  ProviderRole,
  ScheduleCategory,
  CallRoutingSkipReason,
} from './constants';
import {
  BINARY_SIGNED_URL_EXTENSION,
  IVR_AUDIO_FILE_EXTENSION,
  IVR_KEY_ENABLED_EXTENSION,
  IVR_INVALID_AUDIO_FILE_EXTENSION,
  IVR_KEY_AFTER_HOURS_EXTENSION,
  IVR_KEY_DIGIT_EXTENSION,
  IVR_KEY_HOURS_SET_EXTENSION,
  IVR_KEY_MAPPING_EXTENSION,
  IVR_HOURS_SET_NAME_EXTENSION,
  IVR_HOURS_TIMEZONE_EXTENSION,
  IVR_LANGUAGE_SWITCH_KEY_EXTENSION,
  IVR_REPLAY_MENU_KEY_EXTENSION,
  IVR_BACKUP_ROUTING_TARGET_EXTENSION,
  IVR_ON_CALL_BACKUP_TARGET_EXTENSION,
  IVR_PLAYBACK_AUDIO_FILE_EXTENSION,
  IVR_ROUTING_ACTION_TYPE_EXTENSION,
  IVR_ROUTING_TARGET_EXTENSION,
  IVR_KEY_RUNG_EXTENSION,
  IVR_KEY_END_OF_CHAIN_AUDIO_EXTENSION,
  IVR_RUNG_KIND_EXTENSION,
  IVR_RUNG_ROLE_EXTENSION,
  IVR_RUNG_SCOPE_EXTENSION,
  IVR_RUNG_PHONE_NUMBER_EXTENSION,
  IVR_RUNG_PROVIDER_EXTENSION,
  IVR_RUNG_AUDIO_FILE_EXTENSION,
  IVR_TEMPLATE_DESCRIPTION_EXTENSION,
  IVR_TEMPLATE_NAME_EXTENSION,
  SCHEDULING_PARAMETERS_EXTENSION,
} from './extensions';

dayjs.extend(utc);
dayjs.extend(timezone);

export type IvrDigit = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9;

/**
 * @deprecated Superseded by {@link IvrTarget} (BAR-2619). Still the on-the-wire encoding —
 * see {@link IvrRoutingActionType} — and still what the current runtime executes, via
 * {@link toLegacyIvrRoutingAction}, which the admin row and the dual-write still use.
 * Goes away once the legacy extension group stops being written.
 */
export type IvrRoutingAction =
  | { type: typeof IvrRoutingActionType.ROLE_POOL; role: ProviderRole; backupRole?: ProviderRole }
  | {
      type: typeof IvrRoutingActionType.EXTERNAL_NUMBER;
      phoneNumber: string;
      backupPhoneNumber?: string;
    }
  | {
      type: typeof IvrRoutingActionType.SPECIFIC_PROVIDER;
      providerRef: Reference<Practitioner>;
      backupProviderRef?: Reference<Practitioner>;
    }
  | { type: typeof IvrRoutingActionType.CARE_TEAM_BY_ROLE; role: ProviderRole; backupRole?: ProviderRole }
  | { type: typeof IvrRoutingActionType.ON_CALL_NP }
  | { type: typeof IvrRoutingActionType.PLAY_AND_HANGUP; audioFile: Attachment };

/**
 * One rung of a routing chain: a single place a call is offered to before the chain moves on.
 *
 * A rung can be a different kind from the rung above it, which is the whole point — the
 * superseded {@link IvrRoutingAction} typed its backup to match its primary, so "ring the PN
 * owner, then the PAC pool" had no representation. `specific-provider` is not in BAR-2619's
 * sketch of this union but is kept: it is offered by today's admin UI, so dropping the kind
 * would strand any option already configured with it.
 */
export type IvrTarget =
  | { kind: typeof IvrTargetKind.ROLE; role: ProviderRole; scope: IvrTargetScope }
  | { kind: typeof IvrTargetKind.ON_CALL_NP }
  | { kind: typeof IvrTargetKind.EXTERNAL_NUMBER; phoneNumber: string }
  | { kind: typeof IvrTargetKind.SPECIFIC_PROVIDER; providerRef: Reference<Practitioner> }
  | { kind: typeof IvrTargetKind.PLAY_AND_HANGUP; audioFile: Attachment };

export interface IvrKeyMapping {
  digit: IvrDigit;
  /**
   * False takes the option out of the menu while keeping how it was configured, so it can
   * be switched back on without being rebuilt. Absent means enabled.
   */
  enabled?: boolean;
  /**
   * Business-hours set scoping this option. Absent means the option routes via
   * `targets` around the clock, which is how every pre-BAR-2473 mapping reads.
   */
  hoursSetId?: IvrHoursSetId;
  /**
   * In-hours routing chain, or the only chain when `hoursSetId` is absent, walked in order.
   *
   * Empty means the option is unconfigured; `prepareKeyMappingForSave` drops such a mapping
   * rather than persisting a digit that rings nothing.
   */
  targets: IvrTarget[];
  /**
   * Chain walked once `hoursSetId` is closed. Absent means the option is simply not
   * available after hours; callers fall through to the virtual receptionist rather
   * than silently getting the in-hours chain.
   */
  afterHoursTargets?: IvrTarget[];
  /**
   * Goodbye audio for when the chain runs out with nobody having answered. Absent means the
   * call just ends. Played by the BAR-2621 runtime; stored here so the admin UI can set it.
   */
  endOfChainAudio?: Attachment;
}

export interface IvrTemplate {
  templateId: IvrTemplateId;
  name: string;
  description?: string;
  audioFile?: Attachment;
  invalidAudioFile?: Attachment;
  keyMappings: IvrKeyMapping[];
}

export interface IvrBusinessHoursBlock {
  startTime: string;
  endTime: string;
}

/**
 * @deprecated Company-wide hours. Superseded by {@link IvrHoursSet}, which scopes hours to
 * the menu options that reference it. Still the shape the config UI reads and writes;
 * removed once that moves over.
 */
export interface IvrBusinessHoursGroup {
  weekday: IvrBusinessHoursBlock;
  weekend: IvrBusinessHoursBlock;
  languageSwitchKey?: number;
  replayMenuKey?: string;
}

/**
 * A named set of business hours that menu options reference by id.
 *
 * Unlike {@link IvrBusinessHoursGroup} both day blocks are optional: a set covering only
 * weekdays is a legitimate configuration, and is closed at the weekend rather than
 * unparseable. The old parser returned `undefined` when either block was missing, which
 * combined with `checkAvailabilityWindow`'s treat-no-extensions-as-open default to make a
 * half-configured set read as open around the clock.
 */
export interface IvrHoursSet {
  hoursSetId: IvrHoursSetId;
  name: string;
  weekday?: IvrBusinessHoursBlock;
  weekend?: IvrBusinessHoursBlock;
  /** IANA zone the block times are local to. Absent means legacy bare-UTC times. */
  timezone?: string;
}

/**
 * Menu-wide keys, previously stored on the business-hours Schedule.
 *
 * They moved to their own Basic because reading them depended on there being exactly one
 * hours Schedule: `searchOne` over a shared service-category returns an arbitrary match
 * once a second set exists, and `applyBusinessHoursToSchedule` rewrites both extensions
 * on every save, so two sets would take turns clobbering each other.
 */
export interface IvrMenuSettings {
  languageSwitchKey: number;
  replayMenuKey: string;
}

export const DEFAULT_IVR_MENU_SETTINGS: IvrMenuSettings = {
  languageSwitchKey: 9,
  replayMenuKey: '*',
};

export const IVR_WEEKDAY_DAYS = ['mon', 'tue', 'wed', 'thu', 'fri'] as const;
export const IVR_WEEKEND_DAYS = ['sat', 'sun'] as const;

export const DEFAULT_IVR_LANGUAGE_SWITCH_KEY = 9;
export const DEFAULT_IVR_REPLAY_MENU_KEY = '*';

export interface IvrConfigResources {
  schedule: Schedule | undefined;
  templateBasics: Basic[];
  /** Set when the Schedule search failed; the templates may still have loaded. */
  scheduleError: unknown;
  /** Set when the Basic search failed; the Schedule may still have loaded. */
  templatesError: unknown;
}

/**
 * Loads the resources the IVR config page needs. The two searches are independent
 * and settled separately: a caller whose AccessPolicy grants Schedule but not Basic
 * (BAR-2441) still gets the Schedule back, along with the error that explains the
 * missing templates.
 */
export async function fetchIvrConfigResources(medplum: MedplumClient): Promise<IvrConfigResources> {
  const [scheduleResult, templatesResult] = await Promise.allSettled([
    medplum.searchOne('Schedule', {
      'actor:Location.type': `${BARIENDO_CT_SYSTEM}|${LocationType.HEADQUARTERS}`,
      'service-category': `${BARIENDO_CT_SYSTEM}|${ScheduleCategory.IVR_BUSINESS_HOURS}`,
    }),
    medplum.searchResources('Basic', {
      code: `${IVR_BASIC_SYSTEM}|${IVR_TEMPLATE_BASIC_CODING.code}`,
    }),
  ]);

  return {
    schedule: scheduleResult.status === 'fulfilled' ? scheduleResult.value : undefined,
    templateBasics: templatesResult.status === 'fulfilled' ? templatesResult.value : [],
    scheduleError: scheduleResult.status === 'rejected' ? scheduleResult.reason : undefined,
    templatesError: templatesResult.status === 'rejected' ? templatesResult.reason : undefined,
  };
}

export const DEFAULT_IVR_BUSINESS_HOURS_GROUP: IvrBusinessHoursGroup = {
  weekday: {
    startTime: '09:00:00',
    endTime: '17:00:00',
  },
  weekend: {
    startTime: '10:00:00',
    endTime: '14:00:00',
  },
  languageSwitchKey: DEFAULT_IVR_LANGUAGE_SWITCH_KEY,
  replayMenuKey: DEFAULT_IVR_REPLAY_MENU_KEY,
};

const IVR_ROUTING_ACTION_TYPES = new Set<string>(Object.values(IvrRoutingActionType));
const IVR_TARGET_KINDS = new Set<string>(Object.values(IvrTargetKind));
const IVR_TARGET_SCOPES = new Set<string>(Object.values(IvrTargetScope));
const IVR_TEMPLATE_IDS = new Set<string>(Object.values(IvrTemplateId));
const IVR_HOURS_SET_IDS = new Set<string>(Object.values(IvrHoursSetId));
const PROVIDER_ROLES = new Set<string>(Object.values(ProviderRole));

/**
 * Recovers the `Binary/<id>` reference an audio attachment ultimately points at.
 *
 * Medplum hands back a presigned storage URL when a resource is read, so an attachment
 * round-tripped through the config UI ends up holding that URL instead of the reference it
 * was saved with. Those URLs carry an `Expires` parameter: once it lapses Twilio gets a 403,
 * abandons the TwiML document and falls back to the virtual receptionist, so the caller is
 * transferred instead of hearing the recording (BAR-2492).
 *
 * Storage URLs are shaped `/binary/<binaryId>/<blobId>`, which is enough to get back to the
 * durable reference without a data migration.
 */
export function getIvrAudioBinaryReference(url: string | undefined): string | undefined {
  if (!url) {
    return undefined;
  }

  if (url.startsWith('Binary/')) {
    return url;
  }

  // Matches both the storage form (`/binary/<binaryId>/<blobId>`) and the FHIR form
  // (`/Binary/<id>`, optionally followed by `/$value`), either of which can end up on the
  // attachment depending on how the file was uploaded.
  const match = /\/binary\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})(?:[/?#]|$)/i.exec(url);
  return match ? `Binary/${match[1]}` : undefined;
}

/**
 * Strips a saved attachment back to the durable reference.
 *
 * Applied on every write so a presigned URL read from the server can never be persisted, and
 * the cached signed-url extension — a snapshot that is stale the moment it is written — is
 * dropped rather than carried forward.
 */
export function normaliseIvrAudioAttachment(attachment: Attachment | undefined): Attachment | undefined {
  if (!attachment) {
    return undefined;
  }

  const binaryReference = getIvrAudioBinaryReference(attachment.url);
  const extensions = attachment.extension?.filter((ext) => ext.url !== BINARY_SIGNED_URL_EXTENSION);

  return {
    ...attachment,
    url: binaryReference ?? attachment.url,
    extension: extensions?.length ? extensions : undefined,
  };
}

export function isIvrHoursSetId(value: string | undefined): value is IvrHoursSetId {
  return !!value && IVR_HOURS_SET_IDS.has(value);
}

export function assertIvrDigit(n: number): IvrDigit {
  if (!Number.isInteger(n) || n < 0 || n > 9) {
    throw new Error(`Invalid IVR digit: ${n}`);
  }
  return n as IvrDigit;
}

export function isIvrTemplateId(value: string | undefined): value is IvrTemplateId {
  return !!value && IVR_TEMPLATE_IDS.has(value);
}

function isIvrRoutingActionType(value: string): value is IvrRoutingActionType {
  return IVR_ROUTING_ACTION_TYPES.has(value);
}

function isIvrTargetKind(value: string): value is IvrTargetKind {
  return IVR_TARGET_KINDS.has(value);
}

function isIvrTargetScope(value: string): value is IvrTargetScope {
  return IVR_TARGET_SCOPES.has(value);
}

function isProviderRole(value: string): value is ProviderRole {
  return PROVIDER_ROLES.has(value);
}

function getNestedExtension(ext: Extension, url: string): Extension | undefined {
  return ext.extension?.find((nested) => nested.url === url);
}

/**
 * Expands a legacy single-action-plus-backup into the chain it has always meant.
 *
 * `care-team-by-role` expands to **two** rungs per role. `selectProviderForCareTeamByRole`
 * (`bots/src/services/ivr/ivr-routing.ts`) falls back to that same role's pool when the
 * patient has no care-team member holding it, and nothing in the stored configuration says
 * so — "PN owner" has always really meant "PN owner, then PN pool". Materialising it here
 * keeps today's behaviour and makes it something an admin can see and switch off, rather
 * than quietly dropping a rung out of every migrated option.
 */
/**
 * Availability verdicts that skip a rung naming one person.
 *
 * Deliberately narrower than the pool rungs' filter. These two are statements about right
 * now — they are on another call, or they have said do not disturb — so ringing anyway
 * spends the rung's timeout on a phone nobody will pick up. `OFF_SHIFT` is excluded on
 * purpose: it is also what "has no call-availability Schedule at all" returns, and a named
 * target is exactly the person least likely to have one, so gating on it would delete the
 * rung the admin configured by name.
 *
 * Lives here rather than beside the runtime because the route tester has to model the same
 * rule, and the two drifting apart is a tester that lies about the thing it exists to
 * explain — which is precisely what happened between BAR-2621 and BAR-2624.
 */
export const NAMED_PROVIDER_BLOCKING_REASONS: readonly CallRoutingSkipReason[] = [
  CallRoutingSkipReason.IN_ANOTHER_CALL,
  CallRoutingSkipReason.DO_NOT_DISTURB,
];

/** Would the runtime skip a named-provider rung for this verdict? */
export function blocksNamedProviderRung(reason: CallRoutingSkipReason | undefined): boolean {
  return reason !== undefined && NAMED_PROVIDER_BLOCKING_REASONS.includes(reason);
}

export function fromLegacyIvrRoutingAction(action: IvrRoutingAction): IvrTarget[] {
  switch (action.type) {
    case IvrRoutingActionType.ROLE_POOL: {
      const targets: IvrTarget[] = [{ kind: IvrTargetKind.ROLE, role: action.role, scope: IvrTargetScope.POOL }];
      if (action.backupRole) {
        targets.push({ kind: IvrTargetKind.ROLE, role: action.backupRole, scope: IvrTargetScope.POOL });
      }
      return targets;
    }
    case IvrRoutingActionType.CARE_TEAM_BY_ROLE: {
      const targets: IvrTarget[] = [
        { kind: IvrTargetKind.ROLE, role: action.role, scope: IvrTargetScope.OWNER },
        { kind: IvrTargetKind.ROLE, role: action.role, scope: IvrTargetScope.POOL },
      ];
      if (action.backupRole) {
        targets.push(
          { kind: IvrTargetKind.ROLE, role: action.backupRole, scope: IvrTargetScope.OWNER },
          { kind: IvrTargetKind.ROLE, role: action.backupRole, scope: IvrTargetScope.POOL }
        );
      }
      return targets;
    }
    case IvrRoutingActionType.EXTERNAL_NUMBER: {
      const targets: IvrTarget[] = [
        {
          kind: IvrTargetKind.EXTERNAL_NUMBER,
          phoneNumber: action.phoneNumber,
        },
      ];
      if (action.backupPhoneNumber) {
        targets.push({
          kind: IvrTargetKind.EXTERNAL_NUMBER,
          phoneNumber: action.backupPhoneNumber,
        });
      }
      return targets;
    }
    case IvrRoutingActionType.SPECIFIC_PROVIDER: {
      const targets: IvrTarget[] = [
        {
          kind: IvrTargetKind.SPECIFIC_PROVIDER,
          providerRef: action.providerRef,
        },
      ];
      if (action.backupProviderRef) {
        targets.push({
          kind: IvrTargetKind.SPECIFIC_PROVIDER,
          providerRef: action.backupProviderRef,
        });
      }
      return targets;
    }
    case IvrRoutingActionType.ON_CALL_NP:
      return [{ kind: IvrTargetKind.ON_CALL_NP }];
    case IvrRoutingActionType.PLAY_AND_HANGUP:
      return [{ kind: IvrTargetKind.PLAY_AND_HANGUP, audioFile: action.audioFile }];
  }
}

/**
 * Projects a chain back onto the legacy primary-plus-one-backup shape.
 *
 * This is the bridge that lets a chain be stored now and executed by the runtime that
 * exists now: it reads the first two rungs and drops the rest, which is exactly what
 * BAR-2619 says the runtime should keep doing until BAR-2621 walks the whole list. It is
 * also what gets written to the legacy extension group, so a bot on the previous release
 * parses a newly written mapping instead of throwing.
 *
 * An `[owner X, pool X]` pair collapses back to a single `care-team-by-role X`, because
 * that action already performs both steps. Without the collapse the pair would serialise as
 * `care-team-by-role X` with a `pool X` backup the old runtime cannot express, and the
 * migrated chain would lose whatever rung came after it.
 *
 * Neither branch reports a backup whose scope it cannot express. A `role-pool` backup can
 * only be another pool, and a `care-team-by-role` backup is itself owner-then-pool, so a
 * pool rung below an owner rung is left for the chain-walking runtime rather than widened
 * into a care-team call the admin did not configure. The projection is already lossy past
 * two rungs; dropping a rung is the failure mode it is built around, ringing somebody
 * unexpected is not.
 *
 * Returns undefined for an empty chain, which callers read as "nothing to route".
 */
export function toLegacyIvrRoutingAction(targets: IvrTarget[]): IvrRoutingAction | undefined {
  const [first, second] = targets;

  if (!first) {
    return undefined;
  }

  switch (first.kind) {
    case IvrTargetKind.ROLE: {
      if (first.scope === IvrTargetScope.OWNER) {
        const materialisedPool =
          second?.kind === IvrTargetKind.ROLE && second.scope === IvrTargetScope.POOL && second.role === first.role;
        const backup = materialisedPool ? targets[2] : second;
        return {
          type: IvrRoutingActionType.CARE_TEAM_BY_ROLE,
          role: first.role,
          backupRole:
            backup?.kind === IvrTargetKind.ROLE && backup.scope === IvrTargetScope.OWNER ? backup.role : undefined,
        };
      }
      return {
        type: IvrRoutingActionType.ROLE_POOL,
        role: first.role,
        // A `role-pool` backup can only be another pool; an owner rung below a pool rung is
        // left for the chain-walking runtime rather than misreported as a pool.
        backupRole:
          second?.kind === IvrTargetKind.ROLE && second.scope === IvrTargetScope.POOL ? second.role : undefined,
      };
    }
    case IvrTargetKind.EXTERNAL_NUMBER:
      return {
        type: IvrRoutingActionType.EXTERNAL_NUMBER,
        phoneNumber: first.phoneNumber,
        backupPhoneNumber: second?.kind === IvrTargetKind.EXTERNAL_NUMBER ? second.phoneNumber : undefined,
      };
    case IvrTargetKind.SPECIFIC_PROVIDER:
      return {
        type: IvrRoutingActionType.SPECIFIC_PROVIDER,
        providerRef: first.providerRef,
        backupProviderRef: second?.kind === IvrTargetKind.SPECIFIC_PROVIDER ? second.providerRef : undefined,
      };
    case IvrTargetKind.ON_CALL_NP:
      return { type: IvrRoutingActionType.ON_CALL_NP };
    case IvrTargetKind.PLAY_AND_HANGUP:
      return {
        type: IvrRoutingActionType.PLAY_AND_HANGUP,
        audioFile: first.audioFile,
      };
  }
}

function buildRoutingTargetExtension(action: IvrRoutingAction): Extension | undefined {
  switch (action.type) {
    case IvrRoutingActionType.ROLE_POOL:
    case IvrRoutingActionType.CARE_TEAM_BY_ROLE:
      return {
        url: IVR_ROUTING_TARGET_EXTENSION,
        valueString: action.role,
      };
    case IvrRoutingActionType.EXTERNAL_NUMBER:
      return {
        url: IVR_ROUTING_TARGET_EXTENSION,
        valueString: action.phoneNumber,
      };
    case IvrRoutingActionType.SPECIFIC_PROVIDER:
      return {
        url: IVR_ROUTING_TARGET_EXTENSION,
        valueReference: action.providerRef,
      };
    case IvrRoutingActionType.ON_CALL_NP:
      return undefined;
    case IvrRoutingActionType.PLAY_AND_HANGUP:
      return undefined;
  }
}

function buildBackupRoutingTargetExtension(action: IvrRoutingAction): Extension | undefined {
  switch (action.type) {
    case IvrRoutingActionType.ROLE_POOL:
    case IvrRoutingActionType.CARE_TEAM_BY_ROLE:
      if (!action.backupRole) {
        return undefined;
      }
      return {
        url: IVR_BACKUP_ROUTING_TARGET_EXTENSION,
        valueString: action.backupRole,
      };
    case IvrRoutingActionType.EXTERNAL_NUMBER:
      if (!action.backupPhoneNumber) {
        return undefined;
      }
      return {
        url: IVR_BACKUP_ROUTING_TARGET_EXTENSION,
        valueString: action.backupPhoneNumber,
      };
    case IvrRoutingActionType.SPECIFIC_PROVIDER:
      if (!action.backupProviderRef) {
        return undefined;
      }
      return {
        url: IVR_BACKUP_ROUTING_TARGET_EXTENSION,
        valueReference: action.backupProviderRef,
      };
    case IvrRoutingActionType.ON_CALL_NP:
      return undefined;
    case IvrRoutingActionType.PLAY_AND_HANGUP:
      return undefined;
  }
}

/** Serialises one routing action to the action-type/target/backup/playback extension group. */
function buildRoutingActionExtensions(action: IvrRoutingAction): Extension[] {
  const extensions: Extension[] = [
    {
      url: IVR_ROUTING_ACTION_TYPE_EXTENSION,
      valueCode: action.type,
    },
  ];

  const targetExtension = buildRoutingTargetExtension(action);
  if (targetExtension) {
    extensions.push(targetExtension);
  }

  const backupTargetExtension = buildBackupRoutingTargetExtension(action);
  if (backupTargetExtension) {
    extensions.push(backupTargetExtension);
  }

  if (action.type === IvrRoutingActionType.PLAY_AND_HANGUP) {
    extensions.push({
      url: IVR_PLAYBACK_AUDIO_FILE_EXTENSION,
      valueAttachment: normaliseIvrAudioAttachment(action.audioFile),
    });
  }

  return extensions;
}

/** Serialises one rung to a nested kind/role/scope/phone/provider/audio group. */
function buildRungExtension(target: IvrTarget): Extension {
  const nested: Extension[] = [{ url: IVR_RUNG_KIND_EXTENSION, valueCode: target.kind }];

  switch (target.kind) {
    case IvrTargetKind.ROLE:
      nested.push(
        { url: IVR_RUNG_ROLE_EXTENSION, valueString: target.role },
        { url: IVR_RUNG_SCOPE_EXTENSION, valueCode: target.scope }
      );
      break;
    case IvrTargetKind.EXTERNAL_NUMBER:
      nested.push({
        url: IVR_RUNG_PHONE_NUMBER_EXTENSION,
        valueString: target.phoneNumber,
      });
      break;
    case IvrTargetKind.SPECIFIC_PROVIDER:
      nested.push({
        url: IVR_RUNG_PROVIDER_EXTENSION,
        valueReference: target.providerRef,
      });
      break;
    case IvrTargetKind.PLAY_AND_HANGUP:
      nested.push({
        url: IVR_RUNG_AUDIO_FILE_EXTENSION,
        valueAttachment: normaliseIvrAudioAttachment(target.audioFile),
      });
      break;
    case IvrTargetKind.ON_CALL_NP:
      break;
  }

  return { url: IVR_KEY_RUNG_EXTENSION, extension: nested };
}

/**
 * Serialises a chain twice: once as ordered rungs, once as the legacy action group.
 *
 * Both encodings are written for every chain. The legacy group is what a bot on the
 * previous release reads, and a missing one is not a local failure — `parseRoutingAction`
 * throws, which propagates through `parseIvrTemplateBasic` to `selectIvrTemplate`, which
 * returns null and drops every caller to direct routing. Writing it costs a few extensions;
 * omitting it would take the menu down for the length of a rollout.
 */
function buildTargetChainExtensions(targets: IvrTarget[]): Extension[] {
  const legacyAction = toLegacyIvrRoutingAction(targets);

  return [...(legacyAction ? buildRoutingActionExtensions(legacyAction) : []), ...targets.map(buildRungExtension)];
}

function buildKeyMappingExtension(mapping: IvrKeyMapping): Extension {
  // The in-hours chain stays flat at the top level so that a bot running the previous
  // release still parses this mapping. See IVR_KEY_AFTER_HOURS_EXTENSION.
  const nestedExtensions: Extension[] = [
    {
      url: IVR_KEY_DIGIT_EXTENSION,
      valueInteger: mapping.digit,
    },
    ...buildTargetChainExtensions(mapping.targets),
  ];

  // Written only when disabled, so an enabled mapping serialises exactly as before.
  if (mapping.enabled === false) {
    nestedExtensions.push({
      url: IVR_KEY_ENABLED_EXTENSION,
      valueBoolean: false,
    });
  }

  if (mapping.hoursSetId) {
    nestedExtensions.push({
      url: IVR_KEY_HOURS_SET_EXTENSION,
      valueString: mapping.hoursSetId,
    });
  }

  if (mapping.afterHoursTargets?.length) {
    nestedExtensions.push({
      url: IVR_KEY_AFTER_HOURS_EXTENSION,
      extension: buildTargetChainExtensions(mapping.afterHoursTargets),
    });
  }

  if (mapping.endOfChainAudio) {
    nestedExtensions.push({
      url: IVR_KEY_END_OF_CHAIN_AUDIO_EXTENSION,
      valueAttachment: normaliseIvrAudioAttachment(mapping.endOfChainAudio),
    });
  }

  return {
    url: IVR_KEY_MAPPING_EXTENSION,
    extension: nestedExtensions,
  };
}

function parseRoutingAction(
  actionTypeExtension: Extension,
  targetExtension?: Extension,
  onCallBackupTargetExtension?: Extension,
  backupTargetExtension?: Extension,
  playbackAudioFileExtension?: Extension
): IvrRoutingAction {
  const actionType = actionTypeExtension.valueCode;
  if (!actionType || !isIvrRoutingActionType(actionType)) {
    throw new Error(`Invalid IVR routing action type: ${actionType}`);
  }

  switch (actionType) {
    case IvrRoutingActionType.ROLE_POOL: {
      const role = targetExtension?.valueString;
      if (!role || !isProviderRole(role)) {
        throw new Error(`Missing or invalid role for ${actionType}`);
      }
      const backupRole = backupTargetExtension?.valueString;
      return {
        type: IvrRoutingActionType.ROLE_POOL,
        role,
        backupRole: backupRole && isProviderRole(backupRole) ? backupRole : undefined,
      };
    }
    case IvrRoutingActionType.CARE_TEAM_BY_ROLE: {
      const role = targetExtension?.valueString;
      if (!role || !isProviderRole(role)) {
        throw new Error(`Missing or invalid role for ${actionType}`);
      }
      const backupRole = backupTargetExtension?.valueString;
      return {
        type: IvrRoutingActionType.CARE_TEAM_BY_ROLE,
        role,
        backupRole: backupRole && isProviderRole(backupRole) ? backupRole : undefined,
      };
    }
    case IvrRoutingActionType.EXTERNAL_NUMBER: {
      const phoneNumber = targetExtension?.valueString;
      if (!phoneNumber) {
        throw new Error(`Missing phone number for ${actionType}`);
      }
      const backupPhoneNumber = backupTargetExtension?.valueString;
      return {
        type: IvrRoutingActionType.EXTERNAL_NUMBER,
        phoneNumber,
        backupPhoneNumber: backupPhoneNumber || undefined,
      };
    }
    case IvrRoutingActionType.SPECIFIC_PROVIDER: {
      const providerRef = targetExtension?.valueReference;
      if (!providerRef) {
        throw new Error(`Missing provider reference for ${actionType}`);
      }
      const backupProviderRef = backupTargetExtension?.valueReference;
      return {
        type: IvrRoutingActionType.SPECIFIC_PROVIDER,
        providerRef: providerRef as Reference<Practitioner>,
        backupProviderRef: backupProviderRef ? (backupProviderRef as Reference<Practitioner>) : undefined,
      };
    }
    case IvrRoutingActionType.ON_CALL_NP: {
      return { type: IvrRoutingActionType.ON_CALL_NP };
    }
    case IvrRoutingActionType.PLAY_AND_HANGUP: {
      const audioFile = playbackAudioFileExtension?.valueAttachment;
      if (!audioFile) {
        throw new Error('Missing audio file for play-and-hangup');
      }
      return {
        type: IvrRoutingActionType.PLAY_AND_HANGUP,
        audioFile,
      };
    }
  }
}

/** Reads a routing action from a container holding the action-type/target/backup group. */
function parseRoutingActionFromContainer(container: Extension): IvrRoutingAction {
  const actionTypeExtension = getNestedExtension(container, IVR_ROUTING_ACTION_TYPE_EXTENSION);

  if (!actionTypeExtension) {
    throw new Error('Missing IVR routing action type');
  }

  return parseRoutingAction(
    actionTypeExtension,
    getNestedExtension(container, IVR_ROUTING_TARGET_EXTENSION),
    getNestedExtension(container, IVR_ON_CALL_BACKUP_TARGET_EXTENSION),
    getNestedExtension(container, IVR_BACKUP_ROUTING_TARGET_EXTENSION),
    getNestedExtension(container, IVR_PLAYBACK_AUDIO_FILE_EXTENSION)
  );
}

function parseRungExtension(rung: Extension): IvrTarget {
  const kind = getNestedExtension(rung, IVR_RUNG_KIND_EXTENSION)?.valueCode;

  if (!kind || !isIvrTargetKind(kind)) {
    throw new Error(`Invalid IVR routing target kind: ${kind}`);
  }

  switch (kind) {
    case IvrTargetKind.ROLE: {
      const role = getNestedExtension(rung, IVR_RUNG_ROLE_EXTENSION)?.valueString;
      if (!role || !isProviderRole(role)) {
        throw new Error(`Missing or invalid role for ${kind} target`);
      }
      const scope = getNestedExtension(rung, IVR_RUNG_SCOPE_EXTENSION)?.valueCode;
      // An unreadable scope reads as the pool. Owner is the narrower of the two, so
      // guessing it would silently shrink who a broken rung rings.
      return {
        kind,
        role,
        scope: scope && isIvrTargetScope(scope) ? scope : IvrTargetScope.POOL,
      };
    }
    case IvrTargetKind.EXTERNAL_NUMBER: {
      const phoneNumber = getNestedExtension(rung, IVR_RUNG_PHONE_NUMBER_EXTENSION)?.valueString;
      if (!phoneNumber) {
        throw new Error(`Missing phone number for ${kind} target`);
      }
      return { kind, phoneNumber };
    }
    case IvrTargetKind.SPECIFIC_PROVIDER: {
      const providerRef = getNestedExtension(rung, IVR_RUNG_PROVIDER_EXTENSION)?.valueReference;
      if (!providerRef) {
        throw new Error(`Missing provider reference for ${kind} target`);
      }
      return { kind, providerRef: providerRef as Reference<Practitioner> };
    }
    case IvrTargetKind.PLAY_AND_HANGUP: {
      const audioFile = getNestedExtension(rung, IVR_RUNG_AUDIO_FILE_EXTENSION)?.valueAttachment;
      if (!audioFile) {
        throw new Error(`Missing audio file for ${kind} target`);
      }
      return { kind, audioFile };
    }
    case IvrTargetKind.ON_CALL_NP:
      return { kind };
  }
}

/**
 * Reads a routing chain, preferring rungs and falling back to the legacy action group.
 *
 * The fallback is what keeps a half-deployed environment routing: a template written before
 * BAR-2619, or by a provider-app still on the old release, carries only the flat
 * action-type/target/backup-target group, and is expanded through
 * {@link fromLegacyIvrRoutingAction} — including the care-team owner/pool materialisation.
 *
 * A rung this reader cannot parse takes that same fallback. That is the point of writing
 * both encodings: if the new one is unusable, route on the old one. Letting the throw escape
 * instead would not be a local failure — it propagates through `parseIvrTemplateBasic` to
 * `selectIvrTemplate`, which finds no usable BUSINESS_HOURS_EN template, returns null and
 * drops every caller to direct routing. One rung written by a later release, or one hand-
 * edited resource, would take the whole menu down. The escape hatch is only available when
 * the legacy group is actually there — a rungs-only container still throws, because a wrong
 * chain is worse than an absent one.
 */
function parseTargetChainFromContainer(container: Extension): IvrTarget[] {
  const rungs = container.extension?.filter((nested) => nested.url === IVR_KEY_RUNG_EXTENSION) ?? [];

  if (rungs.length > 0) {
    try {
      return rungs.map(parseRungExtension);
    } catch (rungError) {
      if (!getNestedExtension(container, IVR_ROUTING_ACTION_TYPE_EXTENSION)) {
        throw rungError;
      }
    }
  }

  return fromLegacyIvrRoutingAction(parseRoutingActionFromContainer(container));
}

function parseKeyMappingExtension(extension: Extension): IvrKeyMapping {
  const digitExtension = getNestedExtension(extension, IVR_KEY_DIGIT_EXTENSION);

  if (digitExtension?.valueInteger === undefined) {
    throw new Error('Missing IVR key digit');
  }

  const hoursSetId = getNestedExtension(extension, IVR_KEY_HOURS_SET_EXTENSION)?.valueString;
  const afterHoursExtension = getNestedExtension(extension, IVR_KEY_AFTER_HOURS_EXTENSION);

  return {
    digit: assertIvrDigit(digitExtension.valueInteger),
    // Mirrors the writer: present only when off, so an enabled mapping parses back to
    // exactly what was built from it and existing resources are untouched by this field.
    enabled: getNestedExtension(extension, IVR_KEY_ENABLED_EXTENSION)?.valueBoolean === false ? false : undefined,
    // Absent on every mapping written before BAR-2473, which is exactly the
    // "routes the same way around the clock" case.
    hoursSetId: isIvrHoursSetId(hoursSetId) ? hoursSetId : undefined,
    targets: parseTargetChainFromContainer(extension),
    afterHoursTargets: afterHoursExtension ? parseTargetChainFromContainer(afterHoursExtension) : undefined,
    endOfChainAudio: getNestedExtension(extension, IVR_KEY_END_OF_CHAIN_AUDIO_EXTENSION)?.valueAttachment,
  };
}

export function getIvrTemplateId(basic: Basic): IvrTemplateId | undefined {
  const value = getIdentifier(basic, IVR_TEMPLATE_IDENTIFIER_SYSTEM);
  return value && isIvrTemplateId(value) ? value : undefined;
}

export function buildIvrTemplateBasic(template: IvrTemplate): Basic {
  const extensions: Extension[] = [
    {
      url: IVR_TEMPLATE_NAME_EXTENSION,
      valueString: template.name,
    },
    ...template.keyMappings.map(buildKeyMappingExtension),
  ];

  if (template.description) {
    extensions.push({
      url: IVR_TEMPLATE_DESCRIPTION_EXTENSION,
      valueString: template.description,
    });
  }

  if (template.audioFile) {
    extensions.push({
      url: IVR_AUDIO_FILE_EXTENSION,
      valueAttachment: normaliseIvrAudioAttachment(template.audioFile),
    });
  }

  if (template.invalidAudioFile) {
    extensions.push({
      url: IVR_INVALID_AUDIO_FILE_EXTENSION,
      valueAttachment: normaliseIvrAudioAttachment(template.invalidAudioFile),
    });
  }

  return {
    resourceType: 'Basic',
    identifier: [{ system: IVR_TEMPLATE_IDENTIFIER_SYSTEM, value: template.templateId }],
    code: { coding: [IVR_TEMPLATE_BASIC_CODING] },
    extension: extensions,
  };
}

export function parseIvrTemplateBasic(basic: Basic): IvrTemplate {
  const templateId = getIvrTemplateId(basic);

  if (!templateId) {
    throw new Error('Invalid or missing IVR template identifier');
  }

  const name = getExtension(basic, IVR_TEMPLATE_NAME_EXTENSION)?.valueString;
  if (!name) {
    throw new Error('Missing IVR template name');
  }

  const description = getExtension(basic, IVR_TEMPLATE_DESCRIPTION_EXTENSION)?.valueString;

  const keyMappings = (basic.extension ?? [])
    .filter((extension) => extension.url === IVR_KEY_MAPPING_EXTENSION)
    .map(parseKeyMappingExtension);

  return {
    templateId,
    name,
    description,
    audioFile: getExtension(basic, IVR_AUDIO_FILE_EXTENSION)?.valueAttachment,
    invalidAudioFile: getExtension(basic, IVR_INVALID_AUDIO_FILE_EXTENSION)?.valueAttachment,
    keyMappings,
  };
}

function isWeekendDayOfWeek(dayOfWeek: string[]): boolean {
  return dayOfWeek.some((day) => day === 'sat' || day === 'sun');
}

function parseBusinessHoursBlockFromSchedulingExt(schedulingExt: Extension): IvrBusinessHoursBlock | undefined {
  const availabilityExt = getExtension(schedulingExt, 'availability');
  const repeat = availabilityExt?.valueTiming?.repeat;
  if (!repeat?.dayOfWeek?.length || !repeat.timeOfDay?.[0]) {
    return undefined;
  }

  const startTime = normalizeTimeToHHMMSS(repeat.timeOfDay[0]);
  const durationHours = convertTimingDurationToHours(repeat.duration, repeat.durationUnit);
  const endTime = calculateEndTimeFromDuration(startTime, durationHours);

  return { startTime, endTime };
}

function buildSchedulingParametersExtension(dayOfWeek: readonly string[], block: IvrBusinessHoursBlock): Extension {
  const startTime = normalizeTimeToHHMMSS(block.startTime);
  const endTime = normalizeTimeToHHMMSS(block.endTime);
  const duration = calculateDurationHours(startTime, endTime);

  if (duration <= 0) {
    throw new Error('Invalid time range: end time must be after start time');
  }

  return {
    url: SCHEDULING_PARAMETERS_EXTENSION,
    extension: [
      {
        url: 'availability',
        valueTiming: {
          repeat: {
            dayOfWeek: [...dayOfWeek] as ('mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun')[],
            timeOfDay: [startTime],
            duration,
            durationUnit: 'h',
          },
        },
      },
    ],
  };
}

export function parseBusinessHoursFromSchedule(schedule: Schedule): IvrBusinessHoursGroup | undefined {
  const schedulingExtensions = schedule.extension?.filter((ext) => ext.url === SCHEDULING_PARAMETERS_EXTENSION) ?? [];

  if (schedulingExtensions.length === 0) {
    return undefined;
  }

  let weekday: IvrBusinessHoursBlock | undefined;
  let weekend: IvrBusinessHoursBlock | undefined;

  for (const schedulingExt of schedulingExtensions) {
    const availabilityExt = getExtension(schedulingExt, 'availability');
    const dayOfWeek = availabilityExt?.valueTiming?.repeat?.dayOfWeek ?? [];
    const block = parseBusinessHoursBlockFromSchedulingExt(schedulingExt);

    if (!block || dayOfWeek.length === 0) {
      continue;
    }

    if (isWeekendDayOfWeek(dayOfWeek)) {
      weekend = block;
    } else {
      weekday = block;
    }
  }

  if (!weekday || !weekend) {
    return undefined;
  }

  const languageSwitchKey =
    getExtension(schedule, IVR_LANGUAGE_SWITCH_KEY_EXTENSION)?.valueInteger ?? DEFAULT_IVR_LANGUAGE_SWITCH_KEY;

  const replayMenuKey =
    getExtension(schedule, IVR_REPLAY_MENU_KEY_EXTENSION)?.valueString ?? DEFAULT_IVR_REPLAY_MENU_KEY;

  return {
    weekday,
    weekend,
    languageSwitchKey,
    replayMenuKey,
  };
}

export function applyBusinessHoursToSchedule(schedule: Schedule, hours: IvrBusinessHoursGroup): Schedule {
  const preservedExtensions = (schedule.extension ?? []).filter(
    (ext) =>
      ext.url !== SCHEDULING_PARAMETERS_EXTENSION &&
      ext.url !== IVR_LANGUAGE_SWITCH_KEY_EXTENSION &&
      ext.url !== IVR_REPLAY_MENU_KEY_EXTENSION &&
      // This writer emits bare UTC times. Leaving a timezone behind would leave the
      // Schedule claiming those UTC numbers are wall-clock local to that zone, silently
      // shifting the set by the whole UTC offset. Drop it so the set reads as legacy UTC.
      ext.url !== IVR_HOURS_TIMEZONE_EXTENSION
  );

  const languageSwitchKey = hours.languageSwitchKey ?? DEFAULT_IVR_LANGUAGE_SWITCH_KEY;
  const replayMenuKey = hours.replayMenuKey ?? DEFAULT_IVR_REPLAY_MENU_KEY;

  return {
    ...schedule,
    serviceCategory: [{ coding: [IVR_BUSINESS_HOURS_SCHEDULE_CATEGORY] }],
    extension: [
      ...preservedExtensions,
      buildSchedulingParametersExtension(IVR_WEEKDAY_DAYS, hours.weekday),
      buildSchedulingParametersExtension(IVR_WEEKEND_DAYS, hours.weekend),
      {
        url: IVR_LANGUAGE_SWITCH_KEY_EXTENSION,
        valueInteger: languageSwitchKey,
      },
      {
        url: IVR_REPLAY_MENU_KEY_EXTENSION,
        valueString: replayMenuKey,
      },
    ],
  };
}

export function getIvrHoursSetId(schedule: Schedule): IvrHoursSetId | undefined {
  const value = getIdentifier(schedule, IVR_HOURS_SET_IDENTIFIER_SYSTEM);
  return isIvrHoursSetId(value) ? value : undefined;
}

/**
 * Reads one hours set. Unlike {@link parseBusinessHoursFromSchedule} a set with only one
 * day block still parses — it is simply closed on the other days.
 *
 * A Schedule with no hours-set identifier is the pre-BAR-2473 company-wide Schedule and is
 * read as INTAKE, the later-hours set, so behaviour is unchanged until a clinical set exists.
 */
export function parseHoursSetFromSchedule(schedule: Schedule): IvrHoursSet | undefined {
  const hoursSetId = getIvrHoursSetId(schedule) ?? IvrHoursSetId.INTAKE;
  const schedulingExtensions = schedule.extension?.filter((ext) => ext.url === SCHEDULING_PARAMETERS_EXTENSION) ?? [];

  let weekday: IvrBusinessHoursBlock | undefined;
  let weekend: IvrBusinessHoursBlock | undefined;

  for (const schedulingExt of schedulingExtensions) {
    const availabilityExt = getExtension(schedulingExt, 'availability');
    const dayOfWeek = availabilityExt?.valueTiming?.repeat?.dayOfWeek ?? [];
    const block = parseBusinessHoursBlockFromSchedulingExt(schedulingExt);

    if (!block || dayOfWeek.length === 0) {
      continue;
    }

    if (isWeekendDayOfWeek(dayOfWeek)) {
      weekend = block;
    } else {
      weekday = block;
    }
  }

  if (!weekday && !weekend) {
    return undefined;
  }

  return {
    hoursSetId,
    name: getExtension(schedule, IVR_HOURS_SET_NAME_EXTENSION)?.valueString ?? IVR_HOURS_SET_DEFAULT_NAMES[hoursSetId],
    weekday,
    weekend,
    timezone: getExtension(schedule, IVR_HOURS_TIMEZONE_EXTENSION)?.valueString,
  };
}

export function applyHoursSetToSchedule(schedule: Schedule, hoursSet: IvrHoursSet): Schedule {
  const preservedExtensions = (schedule.extension ?? []).filter(
    (ext) =>
      ext.url !== SCHEDULING_PARAMETERS_EXTENSION &&
      ext.url !== IVR_HOURS_SET_NAME_EXTENSION &&
      ext.url !== IVR_HOURS_TIMEZONE_EXTENSION
  );

  const extensions: Extension[] = [
    ...preservedExtensions,
    { url: IVR_HOURS_SET_NAME_EXTENSION, valueString: hoursSet.name },
  ];

  if (hoursSet.weekday) {
    extensions.push(buildSchedulingParametersExtension(IVR_WEEKDAY_DAYS, hoursSet.weekday));
  }

  if (hoursSet.weekend) {
    extensions.push(buildSchedulingParametersExtension(IVR_WEEKEND_DAYS, hoursSet.weekend));
  }

  if (hoursSet.timezone) {
    extensions.push({ url: IVR_HOURS_TIMEZONE_EXTENSION, valueString: hoursSet.timezone });
  }

  const otherIdentifiers = (schedule.identifier ?? []).filter(
    (identifier) => identifier.system !== IVR_HOURS_SET_IDENTIFIER_SYSTEM
  );

  return {
    ...schedule,
    identifier: [...otherIdentifiers, { system: IVR_HOURS_SET_IDENTIFIER_SYSTEM, value: hoursSet.hoursSetId }],
    serviceCategory: [{ coding: [IVR_BUSINESS_HOURS_SCHEDULE_CATEGORY] }],
    extension: extensions,
  };
}

/** Every Schedule holding IVR hours, for callers that need to write one back. */
export async function fetchIvrHoursSetSchedules(medplum: MedplumClient): Promise<Schedule[]> {
  return medplum.searchResources('Schedule', {
    'actor:Location.type': `${BARIENDO_CT_SYSTEM}|${LocationType.HEADQUARTERS}`,
    'service-category': `${BARIENDO_CT_SYSTEM}|${ScheduleCategory.IVR_BUSINESS_HOURS}`,
  });
}

/**
 * The headquarters Location every IVR hours Schedule hangs off.
 *
 * Needed when creating a set that does not exist yet: a Schedule with no HQ actor is
 * invisible to the searches above, so it would save cleanly and then never be read back.
 */
export async function fetchHeadquartersLocation(medplum: MedplumClient): Promise<Location | undefined> {
  return medplum.searchOne('Location', {
    type: `${BARIENDO_CT_SYSTEM}|${LocationType.HEADQUARTERS}`,
  });
}

export async function fetchIvrHoursSets(medplum: MedplumClient): Promise<Map<IvrHoursSetId, IvrHoursSet>> {
  const schedules = await fetchIvrHoursSetSchedules(medplum);

  const hoursSets = new Map<IvrHoursSetId, IvrHoursSet>();

  for (const schedule of schedules) {
    const parsed = parseHoursSetFromSchedule(schedule);

    if (!parsed) {
      continue;
    }

    // An explicitly identified Schedule always beats one that only defaulted to this id,
    // whatever order the search returned them in. Otherwise a leftover unidentified legacy
    // Schedule could displace the real intake set depending on nothing but result ordering.
    const isExplicit = !!getIvrHoursSetId(schedule);
    const existing = hoursSets.get(parsed.hoursSetId);

    if (existing && !isExplicit) {
      continue;
    }

    hoursSets.set(parsed.hoursSetId, parsed);
  }

  return hoursSets;
}

function toMinutesOfDay(time: string): number {
  const [hours = '0', minutes = '0'] = normalizeTimeToHHMMSS(time).split(':');
  return Number(hours) * 60 + Number(minutes);
}

/**
 * Resolves `now` into the set's zone.
 *
 * `dayjs().tz()` throws a RangeError on an unrecognised zone, and this runs inside the
 * Twilio callback: an unhandled throw there propagates through wrapBotWithSentry, which
 * rethrows, so the callback 500s and the caller gets dead air. A single mistyped character
 * in a config field would silence the option for every caller. Returning null instead lets
 * the set resolve as closed, in line with every other uncertain branch here.
 */
function resolveNowInHoursSetZone(hoursSet: IvrHoursSet, now: dayjs.Dayjs): dayjs.Dayjs | null {
  if (!hoursSet.timezone) {
    return now.utc();
  }

  try {
    return now.tz(hoursSet.timezone);
  } catch {
    return null;
  }
}

/**
 * Whether the set is currently open.
 *
 * Times are wall-clock local to `hoursSet.timezone` when one is set, and bare UTC when it
 * is not. Windows that wrap past midnight are supported; a zero-length window is closed.
 *
 * An unusable timezone reports closed rather than falling back to UTC. Falling back would
 * read a 09:00-17:00 Pacific set as 09:00-17:00 UTC, i.e. 01:00-09:00 Pacific — ringing a
 * clinical pool at 2am, the exact outcome this change exists to prevent.
 */
export function isHoursSetOpen(hoursSet: IvrHoursSet, now: dayjs.Dayjs = dayjs()): boolean {
  const local = resolveNowInHoursSetZone(hoursSet, now);

  if (!local) {
    return false;
  }

  const isWeekend = local.day() === 0 || local.day() === 6;
  const block = isWeekend ? hoursSet.weekend : hoursSet.weekday;

  if (!block) {
    return false;
  }

  const current = local.hour() * 60 + local.minute();
  const start = toMinutesOfDay(block.startTime);
  const end = toMinutesOfDay(block.endTime);

  if (start === end) {
    return false;
  }

  return end > start ? current >= start && current < end : current >= start || current < end;
}

/**
 * Picks the routing chain for a pressed key at `now`.
 *
 * Returns `undefined` when the option is closed and has no after-hours chain configured,
 * which the gather handler turns into a virtual-receptionist forward. Every uncertain
 * branch resolves to after-hours rather than in-hours: an hours set that is missing or
 * unparseable means the option is treated as closed, not as open. Failing the other way
 * would ring a clinical pool at 2am, which is the outcome this whole change exists to avoid.
 */
export function resolveKeyMappingTargets(
  mapping: IvrKeyMapping,
  hoursSets: Map<IvrHoursSetId, IvrHoursSet>,
  now: dayjs.Dayjs = dayjs()
): IvrTarget[] | undefined {
  if (!mapping.hoursSetId) {
    return mapping.targets;
  }

  const hoursSet = hoursSets.get(mapping.hoursSetId);

  // A referenced-but-absent set resolves to after-hours rather than in-hours. The caller
  // logs the digit and set id when this leaves it with no chain to run.
  if (!hoursSet) {
    return mapping.afterHoursTargets;
  }

  return isHoursSetOpen(hoursSet, now) ? mapping.targets : mapping.afterHoursTargets;
}

export function buildIvrMenuSettingsBasic(settings: IvrMenuSettings): Basic {
  return {
    resourceType: 'Basic',
    code: { coding: [IVR_SYSTEM_CONFIG_BASIC_CODING] },
    extension: [
      { url: IVR_LANGUAGE_SWITCH_KEY_EXTENSION, valueInteger: settings.languageSwitchKey },
      { url: IVR_REPLAY_MENU_KEY_EXTENSION, valueString: settings.replayMenuKey },
    ],
  };
}

export function parseIvrMenuSettingsBasic(basic: Basic): IvrMenuSettings {
  return {
    languageSwitchKey:
      getExtension(basic, IVR_LANGUAGE_SWITCH_KEY_EXTENSION)?.valueInteger ??
      DEFAULT_IVR_MENU_SETTINGS.languageSwitchKey,
    replayMenuKey:
      getExtension(basic, IVR_REPLAY_MENU_KEY_EXTENSION)?.valueString ?? DEFAULT_IVR_MENU_SETTINGS.replayMenuKey,
  };
}

/**
 * Menu settings, preferring the dedicated config Basic and falling back to whichever hours
 * Schedule still carries them. The fallback keeps this correct while the config UI is still
 * writing settings to the Schedule; it can go once that moves over.
 */
export async function fetchIvrMenuSettings(medplum: MedplumClient): Promise<IvrMenuSettings> {
  const settingsBasic = await medplum.searchOne('Basic', {
    code: `${IVR_BASIC_SYSTEM}|${IVR_SYSTEM_CONFIG_BASIC_CODING.code}`,
  });

  if (settingsBasic) {
    return parseIvrMenuSettingsBasic(settingsBasic);
  }

  const legacySchedule = await medplum.searchOne('Schedule', {
    'actor:Location.type': `${BARIENDO_CT_SYSTEM}|${LocationType.HEADQUARTERS}`,
    'service-category': `${BARIENDO_CT_SYSTEM}|${ScheduleCategory.IVR_BUSINESS_HOURS}`,
  });

  if (!legacySchedule) {
    return { ...DEFAULT_IVR_MENU_SETTINGS };
  }

  return {
    languageSwitchKey:
      getExtension(legacySchedule, IVR_LANGUAGE_SWITCH_KEY_EXTENSION)?.valueInteger ??
      DEFAULT_IVR_MENU_SETTINGS.languageSwitchKey,
    replayMenuKey:
      getExtension(legacySchedule, IVR_REPLAY_MENU_KEY_EXTENSION)?.valueString ??
      DEFAULT_IVR_MENU_SETTINGS.replayMenuKey,
  };
}

/**
 * The mapping a pressed digit routes to, or undefined when nothing answers that key.
 *
 * A disabled option is deliberately indistinguishable from an unconfigured one here, so the
 * gather handler runs its existing invalid-input path — re-prompt, then fall through to the
 * virtual receptionist — rather than needing a second notion of "configured but off".
 */
export function getKeyMapping(template: IvrTemplate, digit: IvrDigit): IvrKeyMapping | undefined {
  const mapping = template.keyMappings.find((item) => item.digit === digit);
  return mapping?.enabled === false ? undefined : mapping;
}

export function isCompleteTarget(target: IvrTarget): boolean {
  switch (target.kind) {
    case IvrTargetKind.ROLE:
      return !!target.role;
    case IvrTargetKind.EXTERNAL_NUMBER:
      return target.phoneNumber.trim().length > 0;
    case IvrTargetKind.SPECIFIC_PROVIDER:
      // The reference, not the object: the admin UI starts a `specific-provider` rung from an
      // empty reference so the practitioner picker has something to render into, and an
      // unpicked rung must read as incomplete rather than as a provider.
      return !!target.providerRef?.reference;
    case IvrTargetKind.ON_CALL_NP:
      return true;
    case IvrTargetKind.PLAY_AND_HANGUP:
      return !!target.audioFile?.url;
  }
}

export function isCompleteMapping(mapping: IvrKeyMapping, languageSwitchKey: number): boolean {
  if (mapping.digit === languageSwitchKey) {
    return false;
  }

  const [first] = mapping.targets;
  return !!first && isCompleteTarget(first);
}

/**
 * Normalises a mapping for persistence, or drops it entirely when its first rung is
 * unusable.
 *
 * Half-filled rungs cost the option those rungs, not its existence — an unfinished
 * after-hours chain, or a rung someone added and has not filled in yet, is dropped and the
 * rest is kept. Rejecting the whole mapping would delete a working menu option because
 * someone had started, but not finished, configuring what happens to it in the evening — a
 * surprising amount of collateral for an incomplete edit.
 */
export function prepareKeyMappingForSave(mapping: IvrKeyMapping, languageSwitchKey: number): IvrKeyMapping | undefined {
  if (!isCompleteMapping(mapping, languageSwitchKey)) {
    return undefined;
  }

  const afterHoursTargets = mapping.afterHoursTargets?.filter(isCompleteTarget);

  return {
    ...mapping,
    targets: mapping.targets.filter(isCompleteTarget),
    // An after-hours chain left with no usable rung is dropped rather than persisted empty,
    // so the option reads as "closed after hours" instead of "rings nothing after hours".
    afterHoursTargets: afterHoursTargets?.length ? afterHoursTargets : undefined,
  };
}
